import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  useMutation,
} from '@tanstack/react-query';
import {
  Alert,
  alpha,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  AddCircleOutlineRounded,
  AttachFileRounded,
  AutoAwesomeRounded,
  CheckCircleRounded,
  CompareArrowsRounded,
  FactCheckRounded,
  ForumRounded,
  HistoryRounded,
  LoginRounded,
  PersonRounded,
  SendRounded,
  TaskAltRounded,
  UpdateRounded,
} from '@mui/icons-material';
import {
  formatOperationalDate,
} from './IndustrialWorkflowDatePicker';
import {
  createIndustrialWorkflowCaseComment,
} from '../services/industrialWorkflowsService';
import type {
  IndustrialWorkflowAssignableUser,
  IndustrialWorkflowCaseEvent,
  IndustrialWorkflowStageDefinition,
} from '../types/industrialWorkflows';

type IndustrialWorkflowCaseActivityPanelProps = {
  caseId?: string;
  currentUserId?: string | null;
  events: IndustrialWorkflowCaseEvent[];
  stages: IndustrialWorkflowStageDefinition[];
  assignableUsers: IndustrialWorkflowAssignableUser[];
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  error?: unknown;
  onCommentCreated?: (
    event: IndustrialWorkflowCaseEvent,
  ) => void;
  onOpenFullHistory?: () => void;
};

type EventVisual = {
  title: string;
  category: string;
  description: string;
  color: string;
  icon: ReactNode;
};

function formatTime(value: string) {
  return new Intl.DateTimeFormat(
    'es-ES',
    {
      hour: '2-digit',
      minute: '2-digit',
    },
  ).format(new Date(value));
}

function getDayKey(value: string) {
  return new Date(value)
    .toISOString()
    .slice(0, 10);
}

function formatDaySeparator(value: string) {
  return new Intl.DateTimeFormat(
    'es-ES',
    {
      dateStyle: 'medium',
    },
  ).format(new Date(value));
}

function getErrorMessage(error: unknown) {
  if (
    typeof error === 'object'
    && error !== null
    && 'response' in error
  ) {
    const response = (
      error as {
        response?: {
          data?: {
            detail?: string;
          };
        };
      }
    ).response;

    if (response?.data?.detail) {
      return response.data.detail;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'No se pudo cargar la actividad.';
}

function readPayloadText(
  payload: Record<string, unknown>,
  key: string,
) {
  const value = payload[key];

  return typeof value === 'string'
    ? value
    : null;
}

function readPayloadArray(
  payload: Record<string, unknown>,
  key: string,
) {
  const value = payload[key];

  return Array.isArray(value)
    ? value
    : [];
}

function formatPayloadValue(value: unknown) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }

  if (typeof value === 'boolean') {
    return value ? 'Sí' : 'No';
  }

  if (Array.isArray(value)) {
    return value.join(', ');
  }

  return String(value);
}

function formatCaseFieldUpdates(
  payload: Record<string, unknown>,
) {
  const updates =
    readPayloadArray(
      payload,
      'case_field_updates',
    );

  if (!updates.length) {
    return 'Se han actualizado datos del expediente desde un parte.';
  }

  return updates
    .slice(0, 4)
    .map((update) => {
      if (
        typeof update !== 'object'
        || update === null
      ) {
        return null;
      }

      const updatePayload =
        update as Record<string, unknown>;

      const label =
        typeof updatePayload.label === 'string'
          ? updatePayload.label
          : typeof updatePayload.code === 'string'
            ? updatePayload.code
            : 'Dato';

      return `${label}: ${formatPayloadValue(
        updatePayload.previous_value,
      )} → ${formatPayloadValue(
        updatePayload.new_value,
      )}`;
    })
    .filter(Boolean)
    .join(' · ');
}

function isCaseCommentEvent(
  event: IndustrialWorkflowCaseEvent,
) {
  return event.event_type === 'case_comment_added';
}

function getCaseCommentText(
  event: IndustrialWorkflowCaseEvent,
) {
  return readPayloadText(
    event.payload ?? {},
    'comment',
  )
  ?? 'Se ha añadido un comentario interno.';
}

const EVENT_TYPE_LABELS: Record<string, string> = {
  schedule_recalculated: 'Planificación recalculada',
  schedule_manual_replanned: 'Replanificación manual',
  stage_deadline_adjusted: 'Plazo operativo de etapa ajustado',
  task_deadline_adjusted: 'Plazo de tarea ajustado',
  external_followup_recorded: 'Seguimiento externo registrado',
};

function formatEventTypeLabel(eventType: string) {
  const mappedLabel =
    EVENT_TYPE_LABELS[eventType];

  if (mappedLabel) {
    return mappedLabel;
  }

  return eventType
    .split('_')
    .filter(Boolean)
    .map((segment) =>
      `${segment.charAt(0).toUpperCase()}${segment.slice(1)}`,
    )
    .join(' ');
}

function buildSystemEventText(
  event: IndustrialWorkflowCaseEvent,
  visual: EventVisual,
) {
  const description =
    visual.description.trim();

  if (visual.title === 'Actividad registrada') {
    return `Tipo de actividad: ${formatEventTypeLabel(
      event.event_type,
    )}.`;
  }

  const shouldUseDescription =
    description
    && description !== visual.title
    && event.event_type !== 'case_updated';

  return shouldUseDescription
    ? description
    : '';
}

function buildEventVisual(
  event: IndustrialWorkflowCaseEvent,
  stageNameById: Map<string, string>,
  actorLabel: string,
): EventVisual {
  const payload = event.payload ?? {};

  if (event.event_type === 'case_created') {
    const stageId = readPayloadText(
      payload,
      'stage_definition_id',
    );

    return {
      title: 'Expediente creado',
      category: 'Inicio',
      description: stageId
        ? `Etapa inicial: ${
            stageNameById.get(stageId)
            ?? 'Etapa inicial'
          }.`
        : 'Se ha creado el expediente.',
      color: '#4ade80',
      icon: (
        <AddCircleOutlineRounded fontSize="small" />
      ),
    };
  }

  if (event.event_type === 'stage_entered') {
    const stageId = readPayloadText(
      payload,
      'stage_definition_id',
    );

    return {
      title: 'Entrada en etapa',
      category: 'Movimiento',
      description: stageId
        ? `El expediente ha entrado en ${
            stageNameById.get(stageId)
            ?? 'la nueva etapa'
          }.`
        : 'El expediente ha entrado en una nueva etapa.',
      color: '#a78bfa',
      icon: <LoginRounded fontSize="small" />,
    };
  }

  if (event.event_type === 'stage_changed') {
    const fromStageId = readPayloadText(
      payload,
      'from_stage_definition_id',
    );

    const toStageId = readPayloadText(
      payload,
      'to_stage_definition_id',
    );

    const fromStageName = fromStageId
      ? stageNameById.get(fromStageId)
      : null;

    const toStageName = toStageId
      ? stageNameById.get(toStageId)
      : null;

    return {
      title: 'Cambio de etapa',
      category: 'Movimiento',
      description:
        fromStageName && toStageName
          ? `${fromStageName} → ${toStageName}`
          : 'El expediente ha cambiado de etapa.',
      color: '#38bdf8',
      icon: (
        <CompareArrowsRounded fontSize="small" />
      ),
    };
  }

  if (event.event_type === 'task_auto_created') {
    const title = readPayloadText(
      payload,
      'title',
    );

    return {
      title: 'Tarea automática creada',
      category: 'Automatización',
      description: title
        ? `Se ha generado la tarea «${title}».`
        : 'Se ha generado una tarea al entrar en la etapa.',
      color: '#c4b5fd',
      icon: <AutoAwesomeRounded fontSize="small" />,
    };
  }

  if (event.event_type === 'task_created') {
    const title = readPayloadText(
      payload,
      'title',
    );

    return {
      title: 'Tarea creada',
      category: 'Tarea',
      description: title
        ? `Se ha creado la tarea «${title}».`
        : 'Se ha creado una nueva tarea.',
      color: '#7dd3fc',
      icon: <TaskAltRounded fontSize="small" />,
    };
  }

  if (event.event_type === 'task_claimed') {
    const title = readPayloadText(
      payload,
      'title',
    );

    return {
      title: 'Tarea asignada',
      category: 'Responsabilidad',
      description: title
        ? `«${title}»`
        : `${actorLabel} se ha asignado una tarea disponible.`,
      color: '#60a5fa',
      icon: <PersonRounded fontSize="small" />,
    };
  }

  if (event.event_type === 'task_updated') {
    const status = readPayloadText(
      payload,
      'status',
    );

    const title = readPayloadText(
      payload,
      'title',
    );

    const taskDescription = title
      ? `«${title}»`
      : '';

    if (status === 'completed') {
      return {
        title: 'Tarea completada',
        category: 'Tarea',
        description:
          taskDescription,
        color: '#4ade80',
        icon: <CheckCircleRounded fontSize="small" />,
      };
    }

    if (status === 'in_progress') {
      return {
        title: 'Tarea iniciada',
        category: 'Tarea',
        description:
          taskDescription,
        color: '#38bdf8',
        icon: <TaskAltRounded fontSize="small" />,
      };
    }

    if (status === 'blocked') {
      return {
        title: 'Tarea bloqueada',
        category: 'Tarea',
        description:
          taskDescription,
        color: '#f87171',
        icon: <UpdateRounded fontSize="small" />,
      };
    }

    if (status === 'cancelled') {
      return {
        title: 'Tarea cancelada',
        category: 'Tarea',
        description:
          taskDescription,
        color: '#94a3b8',
        icon: <UpdateRounded fontSize="small" />,
      };
    }

    return {
      title: 'Tarea actualizada',
      category: 'Tarea',
      description:
        taskDescription,
      color: '#94a3b8',
      icon: <UpdateRounded fontSize="small" />,
    };
  }

  if (event.event_type === 'work_report_auto_created') {
    const name =
      readPayloadText(payload, 'name')
      ?? 'parte de trabajo';

    return {
      title: 'Parte creado automáticamente',
      category: 'Parte',
      description:
        `Se ha generado el parte «${name}».`,
      color: '#c4b5fd',
      icon: <FactCheckRounded fontSize="small" />,
    };
  }

  if (event.event_type === 'work_report_created') {
    const name =
      readPayloadText(payload, 'name')
      ?? 'parte de trabajo';

    return {
      title: 'Parte creado',
      category: 'Parte',
      description:
        `Se ha creado el parte «${name}».`,
      color: '#7dd3fc',
      icon: <FactCheckRounded fontSize="small" />,
    };
  }

  if (event.event_type === 'work_report_updated') {
    return {
      title: 'Parte actualizado',
      category: 'Parte',
      description:
        'Se han guardado cambios en el borrador del parte.',
      color: '#38bdf8',
      icon: <FactCheckRounded fontSize="small" />,
    };
  }

  if (event.event_type === 'work_report_corrected') {
    const reason =
      readPayloadText(payload, 'change_reason');

    return {
      title: 'Parte modificado',
      category: 'Parte',
      description: reason
        ? `Motivo: ${reason}`
        : 'Se ha modificado un parte completado.',
      color: '#fbbf24',
      icon: <FactCheckRounded fontSize="small" />,
    };
  }

  if (event.event_type === 'work_report_completed') {
    return {
      title: 'Parte completado',
      category: 'Parte',
      description:
        'El parte de trabajo se ha completado.',
      color: '#4ade80',
      icon: <FactCheckRounded fontSize="small" />,
    };
  }

  if (event.event_type === 'work_report_cancelled') {
    const reason =
      readPayloadText(payload, 'reason');

    return {
      title: 'Parte cancelado',
      category: 'Parte',
      description: reason
        ? `Motivo: ${reason}`
        : 'El parte de trabajo se ha cancelado.',
      color: '#94a3b8',
      icon: <FactCheckRounded fontSize="small" />,
    };
  }

  if (event.event_type === 'external_followup_recorded') {
    const note = readPayloadText(
      payload,
      'note',
    );

    const nextReviewAt = readPayloadText(
      payload,
      'next_review_at',
    );

    return {
      title: 'Seguimiento externo registrado',
      category: 'Seguimiento',
      description:
        `${
          note
          ?? 'Se ha registrado un seguimiento externo.'
        }${
          nextReviewAt
            ? ` Próxima revisión: ${formatOperationalDate(
                nextReviewAt,
              )}.`
            : ''
        }`,
      color: '#fbbf24',
      icon: <UpdateRounded fontSize="small" />,
    };
  }

  if (
    event.event_type
    === 'case_fields_updated_from_work_report'
  ) {
    return {
      title: 'Datos del expediente actualizados',
      category: 'Expediente',
      description:
        formatCaseFieldUpdates(payload),
      color: '#7dd3fc',
      icon: <UpdateRounded fontSize="small" />,
    };
  }

  if (event.event_type === 'case_updated') {
    return {
      title: 'Expediente actualizado',
      category: 'Expediente',
      description:
        'Se han actualizado los datos del expediente.',
      color: '#94a3b8',
      icon: <UpdateRounded fontSize="small" />,
    };
  }

  return {
    title: 'Actividad registrada',
    category: 'Actividad',
    description:
      'Se ha registrado una acción en el expediente.',
    color: '#94a3b8',
    icon: <HistoryRounded fontSize="small" />,
  };
}

export default function IndustrialWorkflowCaseActivityPanel({
  caseId,
  currentUserId = null,
  events,
  stages,
  assignableUsers,
  isLoading = false,
  isFetching = false,
  isError = false,
  error,
  onCommentCreated,
  onOpenFullHistory,
}: IndustrialWorkflowCaseActivityPanelProps) {
  const stageNameById = useMemo(
    () =>
      new Map(
        stages.map((stage) => [
          stage.id,
          stage.name,
        ]),
      ),
    [stages],
  );

  const userLabelById = useMemo(
    () =>
      new Map(
        assignableUsers.map((user) => [
          user.user_id,
          user.full_name
          || user.email
          || user.user_id,
        ]),
      ),
    [assignableUsers],
  );

  const timelineEndRef =
    useRef<HTMLDivElement | null>(
      null,
    );

  const [
    commentInput,
    setCommentInput,
  ] = useState('');

  const [
    localEvents,
    setLocalEvents,
  ] = useState<IndustrialWorkflowCaseEvent[]>([]);

  const mergedEvents = useMemo(
    () => {
      const byId =
        new Map<string, IndustrialWorkflowCaseEvent>();

      events.forEach((event) => {
        byId.set(
          event.id,
          event,
        );
      });

      localEvents.forEach((event) => {
        byId.set(
          event.id,
          event,
        );
      });

      return Array.from(
        byId.values(),
      );
    },
    [
      events,
      localEvents,
    ],
  );

  const sortedEvents = useMemo(
    () =>
      [...mergedEvents].sort(
        (left, right) =>
          new Date(left.created_at).getTime()
          - new Date(right.created_at).getTime(),
      ),
    [mergedEvents],
  );

  const resolvedCaseId =
    caseId
    ?? sortedEvents[0]?.case_id
    ?? null;

  const createCommentMutation =
    useMutation({
      mutationFn: () => {
        const comment =
          commentInput.trim();

        if (!resolvedCaseId) {
          throw new Error(
            'No se ha podido identificar el expediente para registrar el comentario.',
          );
        }

        if (!comment) {
          throw new Error(
            'Escribe un comentario antes de enviarlo.',
          );
        }

        return createIndustrialWorkflowCaseComment(
          resolvedCaseId,
          {
            comment,
          },
        );
      },

      onSuccess: (event) => {
        setLocalEvents((currentEvents) => [
          event,
          ...currentEvents.filter(
            (currentEvent) =>
              currentEvent.id !== event.id,
          ),
        ]);

        setCommentInput('');

        onCommentCreated?.(
          event,
        );
      },
    });

  const commentError =
    createCommentMutation.error;

  useEffect(
    () => {
      timelineEndRef.current?.scrollIntoView({
        block: 'end',
      });
    },
    [
      sortedEvents.length,
    ],
  );

  return (
    <Stack
      sx={{
        height: 1,
        minHeight: 0,
        bgcolor: 'rgba(2,6,23,0.2)',
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={1}
        sx={{
          px: 1.6,
          py: 1.35,
          borderBottom: `1px solid ${alpha(
            '#94a3b8',
            0.14,
          )}`,
        }}
      >
        <Stack
          direction="row"
          spacing={0.8}
          alignItems="center"
        >
          <ForumRounded sx={{ color: '#38bdf8' }} />

          <Box>
            <Typography
              sx={{
                color: '#f8fafc',
                fontWeight: 950,
              }}
            >
              Actividad
            </Typography>

            <Typography
              sx={{
                color: '#94a3b8',
                fontSize: '0.76rem',
                mt: 0.1,
              }}
            >
              Historial operativo del expediente
            </Typography>
          </Box>
        </Stack>

        {onOpenFullHistory ? (
          <Button
            size="small"
            variant="text"
            startIcon={<HistoryRounded />}
            onClick={onOpenFullHistory}
            sx={{
              textTransform: 'none',
              fontWeight: 850,
              whiteSpace: 'nowrap',
            }}
          >
            Ver completo
          </Button>
        ) : null}
      </Stack>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          px: 1.25,
          py: 1.2,
        }}
      >
        <Stack spacing={1.1}>
          {isFetching ? (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ color: '#94a3b8' }}
            >
              <CircularProgress size={17} />

              <Typography sx={{ fontSize: '0.82rem' }}>
                Cargando actividad...
              </Typography>
            </Stack>
          ) : null}

          {isError ? (
            <Alert severity="error" variant="outlined">
              {getErrorMessage(error)}
            </Alert>
          ) : null}

          {!isLoading
          && !isError
          && !sortedEvents.length ? (
            <Alert severity="info" variant="outlined">
              Todavía no hay actividad registrada.
            </Alert>
          ) : null}

          <Box>
            <Stack spacing={1.6}>
              {sortedEvents.map((event, eventIndex) => {
                const actorLabel = event.actor_user_id
                  ? userLabelById.get(event.actor_user_id)
                    ?? 'Usuario'
                  : 'Sistema';

                const previousEvent =
                  sortedEvents[eventIndex - 1];

                const shouldShowDaySeparator =
                  !previousEvent
                  || getDayKey(previousEvent.created_at)
                    !== getDayKey(event.created_at);

                const isComment =
                  isCaseCommentEvent(
                    event,
                  );

                const isOwnAuthoredEvent =
                  Boolean(
                    currentUserId
                    && event.actor_user_id
                    && event.actor_user_id === currentUserId,
                  );

                const shouldAlignRight =
                  isOwnAuthoredEvent;

                const visual = isComment
                  ? null
                  : buildEventVisual(
                      event,
                      stageNameById,
                      actorLabel,
                    );

                return (
                  <Stack
                    key={event.id}
                    spacing={0.8}
                  >
                    {shouldShowDaySeparator ? (
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        sx={{
                          my:
                            0.25,
                        }}
                      >
                        <Box
                          sx={{
                            flex:
                              1,

                            height:
                              '1px',

                            bgcolor:
                              alpha(
                                '#94a3b8',
                                0.14,
                              ),
                          }}
                        />

                        <Typography
                          sx={{
                            color:
                              '#94a3b8',

                            fontSize:
                              '0.72rem',

                            fontWeight:
                              850,

                            whiteSpace:
                              'nowrap',
                          }}
                        >
                          {formatDaySeparator(
                            event.created_at,
                          )}
                        </Typography>

                        <Box
                          sx={{
                            flex:
                              1,

                            height:
                              '1px',

                            bgcolor:
                              alpha(
                                '#94a3b8',
                                0.14,
                              ),
                          }}
                        />
                      </Stack>
                    ) : null}

                    {isComment ? (
                      <Box
                        sx={{
                          display:
                            'flex',

                          justifyContent:
                            shouldAlignRight
                              ? 'flex-end'
                              : 'flex-start',
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth:
                              '78%',

                            minWidth:
                              0,
                          }}
                        >
                          <Typography
                            sx={{
                              color:
                                '#94a3b8',

                              fontSize:
                                '0.74rem',

                              mb:
                                0.38,

                              textAlign:
                                shouldAlignRight
                                  ? 'right'
                                  : 'left',
                            }}
                          >
                            {
                              isOwnAuthoredEvent
                                ? 'Tú'
                                : actorLabel
                            }
                            {' · '}
                            {
                              formatTime(
                                event.created_at,
                              )
                            }
                          </Typography>

                          <Paper
                            elevation={0}
                            sx={{
                              borderRadius:
                                shouldAlignRight
                                  ? '16px 16px 6px 16px'
                                  : '16px 16px 16px 6px',

                              border:
                                `1px solid ${alpha(
                                  isOwnAuthoredEvent
                                    ? '#38bdf8'
                                    : '#64748b',
                                  isOwnAuthoredEvent
                                    ? 0.26
                                    : 0.16,
                                )}`,

                              bgcolor:
                                isOwnAuthoredEvent
                                  ? alpha(
                                      '#0ea5e9',
                                      0.14,
                                    )
                                  : 'rgba(15,23,42,0.68)',

                              px:
                                1.25,

                              py:
                                1,
                            }}
                          >
                            <Typography
                              sx={{
                                color:
                                  '#f8fafc',

                                fontSize:
                                  '0.88rem',

                                lineHeight:
                                  1.5,

                                whiteSpace:
                                  'pre-wrap',

                                overflowWrap:
                                  'anywhere',
                              }}
                            >
                              {
                                getCaseCommentText(
                                  event,
                                )
                              }
                            </Typography>
                          </Paper>
                        </Box>
                      </Box>
                    ) : visual ? (
                      <Box
                        sx={{
                          display:
                            'flex',

                          justifyContent:
                            isOwnAuthoredEvent
                              ? 'flex-end'
                              : 'flex-start',
                        }}
                      >
                        <Box
                          sx={{
                            maxWidth:
                              '78%',

                            minWidth:
                              0,
                          }}
                        >
                          <Typography
                            sx={{
                              color:
                                '#94a3b8',

                              fontSize:
                                '0.74rem',

                              mb:
                                0.38,

                              textAlign:
                                isOwnAuthoredEvent
                                  ? 'right'
                                  : 'left',
                            }}
                          >
                            {
                              isOwnAuthoredEvent
                                ? 'Tú'
                                : actorLabel
                            }
                            {' · '}
                            {
                              formatTime(
                                event.created_at,
                              )
                            }
                          </Typography>

                          <Paper
                            elevation={0}
                            sx={{
                              borderRadius:
                                isOwnAuthoredEvent
                                  ? '16px 16px 6px 16px'
                                  : '16px 16px 16px 6px',

                              border:
                                `1px solid ${alpha(
                                  event.actor_user_id
                                    ? visual.color
                                    : '#64748b',
                                  event.actor_user_id
                                    ? 0.18
                                    : 0.14,
                                )}`,

                              bgcolor:
                                event.actor_user_id
                                  ? alpha(
                                      visual.color,
                                      0.08,
                                    )
                                  : 'rgba(15,23,42,0.48)',

                              px:
                                1.15,

                              py:
                                0.9,
                            }}
                          >
                            <Stack
                              spacing={0.45}
                            >
                              <Stack
                                direction="row"
                                spacing={0.7}
                                alignItems="center"
                                flexWrap="wrap"
                                useFlexGap
                                justifyContent={
                                  shouldAlignRight
                                    ? 'flex-end'
                                    : 'flex-start'
                                }
                              >
                                <Box
                                  sx={{
                                    color:
                                      visual.color,

                                    display:
                                      'inline-flex',
                                  }}
                                >
                                  {visual.icon}
                                </Box>

                                <Typography
                                  sx={{
                                    color:
                                      '#f8fafc',

                                    fontSize:
                                      '0.86rem',

                                    fontWeight:
                                      850,

                                    lineHeight:
                                      1.35,

                                    textAlign:
                                      shouldAlignRight
                                        ? 'right'
                                        : 'left',
                                  }}
                                >
                                  {visual.title}
                                </Typography>
                              </Stack>

                              <Typography
                                sx={{
                                  color:
                                    '#cbd5e1',

                                  fontSize:
                                    '0.82rem',

                                  lineHeight:
                                    1.45,

                                  textAlign:
                                    isOwnAuthoredEvent
                                      ? 'right'
                                      : 'left',

                                  overflowWrap:
                                    'anywhere',
                                }}
                              >
                                {
                                  buildSystemEventText(
                                    event,
                                    visual,
                                  )
                                }
                              </Typography>
                            </Stack>
                          </Paper>
                        </Box>
                      </Box>
                    ) : null}
                  </Stack>
                );
              })}

              <Box
                ref={timelineEndRef}
                sx={{
                  height: 1,
                }}
              />
            </Stack>
          </Box>
        </Stack>
      </Box>

      <Divider
        sx={{
          borderColor: alpha('#94a3b8', 0.14),
        }}
      />

      <Stack
        spacing={0.8}
        sx={{
          px: 1.25,
          py: 1.15,
          bgcolor: 'rgba(15,23,42,0.42)',
        }}
      >
        {commentError ? (
          <Alert
            severity="error"
            variant="outlined"
          >
            {getErrorMessage(commentError)}
          </Alert>
        ) : null}

        <TextField
          size="small"
          placeholder="Escribe un comentario..."
          value={commentInput}
          onChange={(event) =>
            setCommentInput(
              event.target.value,
            )
          }
          disabled={
            createCommentMutation.isPending
            || !resolvedCaseId
          }
          multiline
          minRows={2}
          fullWidth
        />

        <Stack
          direction="row"
          justifyContent="space-between"
          spacing={1}
          alignItems="center"
        >
          <Button
            size="small"
            startIcon={<AttachFileRounded />}
            disabled
            sx={{ textTransform: 'none' }}
          >
            Adjuntar archivo
          </Button>

          <Stack
            direction="row"
            spacing={0.8}
            alignItems="center"
          >
            <Typography
              sx={{
                color: '#64748b',
                fontSize: '0.72rem',
                textAlign: 'right',
                maxWidth: 140,
              }}
            >
              Adjuntos disponibles próximamente
            </Typography>

            <Button
              size="small"
              variant="contained"
              endIcon={<SendRounded />}
              onClick={() =>
                createCommentMutation.mutate()
              }
              disabled={
                createCommentMutation.isPending
                || !commentInput.trim()
                || !resolvedCaseId
              }
              sx={{
                textTransform: 'none',
                fontWeight: 850,
              }}
            >
              {createCommentMutation.isPending
                ? 'Enviando...'
                : 'Enviar'}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
}
